import { useState, useEffect } from "react";
import { toast } from "sonner";
import { getDashboard } from "@/lib/api";
import EbitdaModoSwitch, { useEbitdaModo } from "./EbitdaModoSwitch";
import { formatCurrency, formatPercent, formatNumber } from "@/lib/utils";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  TrendingUp,
  DollarSign,
  BarChart3,
  Activity,
  Droplets,
  Shield,
  CreditCard,
  Clock,
  Users,
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart as RechartsPie,
  Pie,
  Cell,
  Legend,
  LabelList,
} from "recharts";
import { CHART, axisProps, gridProps, ChartTooltip, ChartLegend, barCursor, fmtCompact } from "@/components/ui/chart-kit";

export default function DashboardContent({ ejercicioId }) {
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [empresaId, setEmpresaId] = useState(null);
  const [modo, setModo] = useEbitdaModo(empresaId);

  useEffect(() => {
    loadDashboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ejercicioId, modo]);

  const loadDashboard = async () => {
    setLoading(true);
    try {
      const response = await getDashboard(ejercicioId, modo);
      setDashboard(response.data);
      if (response.data?.ejercicio?.empresa_id) setEmpresaId(response.data.ejercicio.empresa_id);
    } catch (error) {
      toast.error("Error al cargar dashboard");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const kpis = dashboard?.kpis || {};
  const ratiosLiquidez = dashboard?.ratios_liquidez || {};
  // Motor ÚNICO de NOF (F3/F5, oct-2026): el mismo cálculo que la pestaña NOF
  // (exclusiones del usuario y bases limpias incluidas).
  const nofModulo = dashboard?.nof_modulo || {};
  const ratiosSolvencia = dashboard?.ratios_solvencia || {};
  const ratiosEndeudamiento = dashboard?.ratios_endeudamiento || {};
  const ratiosRotacion = dashboard?.ratios_rotacion || {};
  const ratiosPersonal = dashboard?.ratios_personal || {};
  const balanceResumen = dashboard?.balance_resumen || {};
  const pygResumen = dashboard?.pyg_resumen || {};

  // Data for charts
  const pygChartData = [
    { name: "Ventas", value: kpis.ventas || 0, fill: CHART.gunmetal },
    { name: "EBITDA", value: kpis.ebitda || 0, fill: CHART.mostaza },
    { name: "EBIT", value: kpis.ebit || 0, fill: CHART.topaz },
    { name: "Resultado", value: kpis.resultado_neto || 0, fill: kpis.resultado_neto >= 0 ? CHART.profit : CHART.loss },
  ];

  const pasivoChartData = [
    { name: "Patrimonio Neto", value: balanceResumen.patrimonio_neto || 0, fill: CHART.mostaza },
    { name: "Pasivo No Corriente", value: balanceResumen.pasivo_no_corriente || 0, fill: CHART.bronce },
    { name: "Pasivo Corriente", value: balanceResumen.pasivo_corriente || 0, fill: CHART.gunmetal },
  ];
  const totalPnPasivo = pasivoChartData.reduce((a, d) => a + (d.value || 0), 0);

  return (
    <div className="space-y-6">
      {/* Switch de EBITDA: afecta los ratios que dependen del EBITDA */}
      <div className="flex items-center justify-between gap-2">
        {/* F4 (oct-2026): aviso de ejercicio PARCIAL — los plazos usan los
            días reales del período y los ratios saldo/flujo van anualizados. */}
        {dashboard?.periodo?.parcial ? (
          <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-300 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/30 px-3 py-1 text-xs font-medium text-amber-700 dark:text-amber-400"
            title={`Período importado: ${dashboard.periodo.fecha_inicio || "?"} → ${dashboard.periodo.fecha_fin || "?"}. Plazos sobre ${dashboard.periodo.dias} días; DFN/EBITDA, Deuda/CF, ROE y ROA anualizados (×365/${dashboard.periodo.dias}).`}
            data-testid="badge-ejercicio-parcial">
            ⚠ Ejercicio parcial: {dashboard.periodo.dias} días (ratios anualizados)
          </span>
        ) : <span />}
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">Ratios vs EBITDA:</span>
          <EbitdaModoSwitch modo={modo} onChange={setModo} />
        </div>
      </div>
      {/* KPI Cards - Ventas, EBITDA, EBIT, Resultado Neto, Cash Flow */}
      <div className="grid gap-4 grid-cols-[repeat(auto-fit,minmax(12.5rem,1fr))]">
        <KPICard
          title="Ventas"
          value={formatCurrency(kpis.ventas)}
          icon={DollarSign}
          trend={null}
        />
        <KPICard
          title="EBITDA"
          value={formatCurrency(kpis.ebitda)}
          subtitle={kpis.margen_ebitda ? `Margen: ${formatPercent(kpis.margen_ebitda)}` : null}
          icon={TrendingUp}
          trend={kpis.ebitda >= 0 ? "up" : "down"}
        />
        <KPICard
          title="EBIT"
          value={formatCurrency(kpis.ebit)}
          subtitle={kpis.margen_ebit ? `Margen: ${formatPercent(kpis.margen_ebit)}` : null}
          icon={BarChart3}
          trend={kpis.ebit >= 0 ? "up" : "down"}
        />
        <KPICard
          title="Resultado Neto"
          value={formatCurrency(kpis.resultado_neto)}
          subtitle={kpis.margen_neto ? `Margen: ${formatPercent(kpis.margen_neto)}` : null}
          icon={Activity}
          trend={kpis.resultado_neto >= 0 ? "up" : "down"}
        />
        <KPICard
          title="Cash Flow"
          value={formatCurrency(pygResumen.cash_flow)}
          subtitle="Resultado + Amortizaciones"
          icon={Activity}
          trend={pygResumen.cash_flow >= 0 ? "up" : "down"}
        />
      </div>

      {/* Charts Row - Evolución PyG y Estructura Financiera */}
      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Evolución PyG</CardTitle>
            <CardDescription>Principales magnitudes de la cuenta de resultados</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[250px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={pygChartData} layout="vertical" margin={{ top: 4, right: 64, left: 0, bottom: 0 }} barCategoryGap="28%">
                  <CartesianGrid {...gridProps} vertical horizontal={false} />
                  <XAxis type="number" {...axisProps} tickFormatter={fmtCompact} />
                  <YAxis type="category" dataKey="name" width={84} {...axisProps} tick={{ ...axisProps.tick, fill: CHART.texto, fontWeight: 500 }} />
                  <Tooltip cursor={barCursor} content={<ChartTooltip hideLabel formatter={(value) => formatCurrency(value)} />} />
                  <Bar dataKey="value" name="Importe" radius={[0, 8, 8, 0]} background={{ fill: CHART.muted, fillOpacity: 0.07, radius: 8 }}>
                    {pygChartData.map((d) => (
                      <Cell key={d.name} fill={d.fill} />
                    ))}
                    <LabelList
                      dataKey="value"
                      position="right"
                      formatter={(v) => `${fmtCompact(v)} €`}
                      style={{ fill: CHART.texto, fontSize: 12, fontWeight: 500 }}
                    />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Estructura Financiera</CardTitle>
            <CardDescription>Distribución de Patrimonio Neto y Pasivo</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[250px]">
              <ResponsiveContainer width="100%" height="100%">
                <RechartsPie>
                  <Pie
                    data={pasivoChartData}
                    cx="50%"
                    cy="45%"
                    innerRadius="62%"
                    outerRadius="86%"
                    paddingAngle={3}
                    cornerRadius={8}
                    stroke="none"
                    dataKey="value"
                    nameKey="name"
                  >
                    {pasivoChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Pie>
                  {/* Total en el centro del donut */}
                  <text x="50%" y="42%" textAnchor="middle" dominantBaseline="middle" style={{ fill: CHART.muted, fontSize: 12 }}>
                    PN + Pasivo
                  </text>
                  <text x="50%" y="50%" textAnchor="middle" dominantBaseline="middle" style={{ fill: CHART.texto, fontSize: 18, fontWeight: 600 }}>
                    {fmtCompact(totalPnPasivo)} €
                  </text>
                  <Tooltip
                    content={
                      <ChartTooltip
                        hideLabel
                        formatter={(value) =>
                          `${formatCurrency(value)} · ${totalPnPasivo ? Math.round((value / totalPnPasivo) * 100) : 0} %`
                        }
                      />
                    }
                  />
                  <Legend content={<ChartLegend />} />
                </RechartsPie>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Resumen Balance y Resumen PyG (arriba) */}
      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Resumen Balance</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {/* ACTIVO */}
              <div className="flex justify-between items-center border-b pb-2">
                <span className="font-medium">ACTIVO</span>
                <span className="font-mono font-bold text-blue-600">{formatCurrency(balanceResumen.activo_total)}</span>
              </div>
              <div className="flex justify-between items-center pl-4">
                <span className="text-muted-foreground">Activo No Corriente</span>
                <span className="font-mono">{formatCurrency(balanceResumen.activo_no_corriente)}</span>
              </div>
              <div className="flex justify-between items-center pl-4">
                <span className="text-muted-foreground">Activo Corriente</span>
                <span className="font-mono">{formatCurrency(balanceResumen.activo_corriente)}</span>
              </div>
              <div className="flex justify-between items-center pl-8 text-sm">
                <span className="text-muted-foreground">- Existencias</span>
                <span className="font-mono">{formatCurrency(balanceResumen.existencias)}</span>
              </div>
              <div className="flex justify-between items-center pl-8 text-sm">
                <span className="text-muted-foreground">- Clientes</span>
                <span className="font-mono">{formatCurrency(balanceResumen.clientes)}</span>
              </div>
              <div className="flex justify-between items-center pl-8 text-sm">
                <span className="text-muted-foreground">- Tesorería</span>
                <span className="font-mono">{formatCurrency(balanceResumen.tesoreria)}</span>
              </div>
              
              {/* PATRIMONIO NETO Y PASIVO */}
              <div className="flex justify-between items-center border-t border-b pt-3 pb-2 mt-3">
                <span className="font-medium">PATRIMONIO NETO + PASIVO</span>
                <span className="font-mono font-bold text-blue-600">{formatCurrency(balanceResumen.pasivo_total)}</span>
              </div>
              <div className="flex justify-between items-center pl-4">
                <span className="text-muted-foreground">Patrimonio Neto</span>
                <span className="font-mono">{formatCurrency(balanceResumen.patrimonio_neto)}</span>
              </div>
              <div className="flex justify-between items-center pl-4">
                <span className="text-muted-foreground">Pasivo No Corriente</span>
                <span className="font-mono">{formatCurrency(balanceResumen.pasivo_no_corriente)}</span>
              </div>
              <div className="flex justify-between items-center pl-4">
                <span className="text-muted-foreground">Pasivo Corriente</span>
                <span className="font-mono">{formatCurrency(balanceResumen.pasivo_corriente)}</span>
              </div>
              <div className="flex justify-between items-center pl-8 text-sm">
                <span className="text-muted-foreground">- Proveedores</span>
                <span className="font-mono">{formatCurrency(balanceResumen.proveedores)}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Resumen PyG */}
        <Card>
          <CardHeader>
            <CardTitle>Resumen PyG</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 text-sm">
              {/* Ingresos */}
              <div className="flex justify-between items-center">
                <span className="font-medium">1. Importe neto cifra negocios</span>
                <span className="font-mono font-bold">{formatCurrency(pygResumen.ventas)}</span>
              </div>
              {pygResumen.var_existencias !== 0 && (
                <div className="flex justify-between items-center pl-4">
                  <span className="text-muted-foreground">2. Variación existencias PT/PC</span>
                  <span className="font-mono">{formatCurrency(pygResumen.var_existencias)}</span>
                </div>
              )}
              {pygResumen.trabajos_activo !== 0 && (
                <div className="flex justify-between items-center pl-4">
                  <span className="text-muted-foreground">3. Trabajos para el activo</span>
                  <span className="font-mono">{formatCurrency(pygResumen.trabajos_activo)}</span>
                </div>
              )}
              {/* Gastos */}
              <div className="flex justify-between items-center pl-4">
                <span className="text-muted-foreground">4. Aprovisionamientos</span>
                <span className="font-mono text-rose-600">{formatCurrency(pygResumen.aprovisionamientos)}</span>
              </div>
              {pygResumen.otros_ingresos !== 0 && (
                <div className="flex justify-between items-center pl-4">
                  <span className="text-muted-foreground">5. Otros ingresos explotación</span>
                  <span className="font-mono">{formatCurrency(pygResumen.otros_ingresos)}</span>
                </div>
              )}
              <div className="flex justify-between items-center pl-4">
                <span className="text-muted-foreground">6. Gastos de personal</span>
                <span className="font-mono text-rose-600">{formatCurrency(-Math.abs(pygResumen.gastos_personal || 0))}</span>
              </div>
              <div className="flex justify-between items-center pl-4">
                <span className="text-muted-foreground">7. Otros gastos explotación</span>
                <span className="font-mono text-rose-600">{formatCurrency(pygResumen.otros_gastos)}</span>
              </div>
              {pygResumen.excesos != null && pygResumen.excesos !== 0 && (
                <div className="flex justify-between items-center pl-4">
                  <span className="text-muted-foreground">10. Excesos de provisiones</span>
                  <span className="font-mono">{formatCurrency(pygResumen.excesos)}</span>
                </div>
              )}
              {/* EBITDA (F6: partidas 1-7 + 10; la 8 y las 9/11/12 van debajo y entran al A.1) */}
              <div className="flex justify-between items-center border-t pt-2 mt-2">
                <span className="font-medium">EBITDA</span>
                <span className={`font-mono font-semibold ${pygResumen.ebitda >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                  {formatCurrency(pygResumen.ebitda)}
                </span>
              </div>
              {pygResumen.amortizaciones !== 0 && (
                <div className="flex justify-between items-center pl-4">
                  <span className="text-muted-foreground">8. Amortizaciones</span>
                  <span className="font-mono text-rose-600">{formatCurrency(-Math.abs(pygResumen.amortizaciones || 0))}</span>
                </div>
              )}
              {/* Partidas fuera del EBITDA (F6): sin ellas, EBITDA − 8 no daba el A.1 que se pinta debajo */}
              {pygResumen.deterioros != null && pygResumen.deterioros !== 0 && (
                <div className="flex justify-between items-center pl-4">
                  <span className="text-muted-foreground">9. Imputación de subvenciones</span>
                  <span className="font-mono">{formatCurrency(pygResumen.deterioros)}</span>
                </div>
              )}
              {pygResumen.deterioro_enajenaciones != null && pygResumen.deterioro_enajenaciones !== 0 && (
                <div className="flex justify-between items-center pl-4">
                  <span className="text-muted-foreground">11. Deterioro y rdo. enajenaciones inmovilizado</span>
                  <span className="font-mono">{formatCurrency(pygResumen.deterioro_enajenaciones)}</span>
                </div>
              )}
              {pygResumen.otros_resultados != null && pygResumen.otros_resultados !== 0 && (
                <div className="flex justify-between items-center pl-4">
                  <span className="text-muted-foreground">12. Otros resultados</span>
                  <span className="font-mono">{formatCurrency(pygResumen.otros_resultados)}</span>
                </div>
              )}
              {/* Resultado Explotación */}
              <div className="flex justify-between items-center border-t pt-2">
                <span className="font-medium">A.1) Resultado Explotación</span>
                <span className={`font-mono ${pygResumen.resultado_explotacion >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                  {formatCurrency(pygResumen.resultado_explotacion)}
                </span>
              </div>
              {/* Resultado Financiero */}
              <div className="flex justify-between items-center">
                <span className="font-medium">A.2) Resultado Financiero</span>
                <span className={`font-mono ${pygResumen.resultado_financiero >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                  {formatCurrency(pygResumen.resultado_financiero)}
                </span>
              </div>
              {/* RAI */}
              <div className="flex justify-between items-center border-t pt-2">
                <span className="font-medium">A.3) Resultado Antes Impuestos</span>
                <span className={`font-mono ${pygResumen.resultado_antes_impuestos >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                  {formatCurrency(pygResumen.resultado_antes_impuestos)}
                </span>
              </div>
              {/* Resultado del Ejercicio */}
              <div className="flex justify-between items-center border-t pt-2 bg-muted/50 px-2 py-1 rounded">
                <span className="font-medium">A.4) Resultado del Ejercicio</span>
                <span className={`font-mono font-bold ${pygResumen.resultado_ejercicio >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                  {formatCurrency(pygResumen.resultado_ejercicio)}
                </span>
              </div>
              {/* Cash Flow */}
              <div className="flex justify-between items-center mt-3 bg-blue-50 dark:bg-blue-950 px-2 py-1 rounded">
                <span className="font-medium">Cash Flow (Rdo + Amort)</span>
                <span className="font-mono font-semibold">{formatCurrency(pygResumen.cash_flow)}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* DFN y EBITDA Base vs Ajustado (abajo) */}
      <div className="grid gap-4 md:grid-cols-2">
        {/* Deuda Financiera Neta */}
        <Card>
          <CardHeader>
            <CardTitle>Deuda Financiera Neta (DFN)</CardTitle>
            <CardDescription>Análisis de la posición de deuda de la empresa</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">+ Deuda Financiera a Largo Plazo</span>
                <span className="font-mono">{formatCurrency(balanceResumen.deuda_financiera_lp)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">+ Deuda Financiera a Corto Plazo</span>
                <span className="font-mono">{formatCurrency(balanceResumen.deuda_financiera_cp)}</span>
              </div>
              <div className="flex justify-between items-center border-t pt-2">
                <span className="font-medium">= Deuda Financiera Total</span>
                <span className="font-mono font-semibold">{formatCurrency(balanceResumen.deuda_financiera_total)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">- Tesorería disponible</span>
                <span className="font-mono text-emerald-600">({formatCurrency(balanceResumen.tesoreria)})</span>
              </div>
              <div className="flex justify-between items-center bg-muted/50 px-3 py-2 rounded-lg mt-2">
                <span className="font-bold">= DEUDA FINANCIERA NETA</span>
                <span className={`font-mono font-bold text-lg ${balanceResumen.dfn > 0 ? 'text-rose-600' : 'text-emerald-600'}`}>
                  {formatCurrency(balanceResumen.dfn)}
                </span>
              </div>
              
              {/* Interpretación */}
              <div className="mt-4 p-3 bg-muted/30 rounded-lg text-sm">
                <p className="font-medium mb-1">Interpretación general:</p>
                <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                  <li><span className="text-emerald-600">DFN negativa</span>: Tesorería cubre deuda</li>
                  <li><span className="text-amber-600">DFN entre 3x y 4x EBITDA</span>: Vigilar</li>
                  <li><span className="text-rose-600">DFN {">"} 4x EBITDA</span>: Apalancamiento elevado</li>
                </ul>
                <p className="mt-2 text-xs italic text-muted-foreground">* Adaptar según sector</p>
                {ratiosEndeudamiento.dfn_ebitda != null && (
                  <p className="mt-2 font-medium">
                    Ratio DFN/EBITDA: <span className={ratiosEndeudamiento.dfn_ebitda > 4 ? 'text-rose-600' : ratiosEndeudamiento.dfn_ebitda > 3 ? 'text-amber-600' : 'text-emerald-600'}>
                      {formatNumber(ratiosEndeudamiento.dfn_ebitda)}x
                    </span>
                  </p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
        {/* EBITDA Base vs Ajustado */}
        <Card data-testid="ebitda-comparison-card">
          <CardHeader>
            <CardTitle>EBITDA Base vs Ajustado</CardTitle>
            <CardDescription>Comparativa del EBITDA calculado y el EBITDA normalizado</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 gap-6">
              {/* EBITDA Base */}
              <div className="space-y-3">
                <div className="text-center p-4 bg-blue-50 dark:bg-blue-950/30 rounded-lg">
                  <p className="text-sm text-muted-foreground mb-1">EBITDA Base</p>
                  <p className={`text-2xl font-mono font-bold ${kpis.ebitda >= 0 ? 'text-blue-600' : 'text-rose-600'}`}>
                    {formatCurrency(kpis.ebitda)}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Margen: {kpis.margen_ebitda != null ? formatPercent(kpis.margen_ebitda) : 'N/D'}
                  </p>
                </div>
              </div>
              
              {/* EBITDA Ajustado */}
              <div className="space-y-3">
                <div className="text-center p-4 bg-emerald-50 dark:bg-emerald-950/30 rounded-lg border-2 border-emerald-200 dark:border-emerald-800">
                  <p className="text-sm text-muted-foreground mb-1">EBITDA Ajustado</p>
                  <p className={`text-2xl font-mono font-bold ${kpis.ebitda_ajustado >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                    {formatCurrency(kpis.ebitda_ajustado)}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Margen: {kpis.margen_ebitda_ajustado != null ? formatPercent(kpis.margen_ebitda_ajustado) : 'N/D'}
                  </p>
                </div>
              </div>
            </div>
            
            {/* Diferencia */}
            {kpis.ebitda !== kpis.ebitda_ajustado && (
              <div className="mt-4 p-3 bg-muted/50 rounded-lg">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium">Diferencia (Ajustes aplicados)</span>
                  <span className={`font-mono font-semibold ${(kpis.ebitda_ajustado - kpis.ebitda) >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                    {(kpis.ebitda_ajustado - kpis.ebitda) >= 0 ? '+' : ''}{formatCurrency(kpis.ebitda_ajustado - kpis.ebitda)}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Configura los ajustes en la pestaña de EBITDA
                </p>
              </div>
            )}
            
            {kpis.ebitda === kpis.ebitda_ajustado && (
              <div className="mt-4 p-3 bg-muted/30 rounded-lg text-center">
                <p className="text-sm text-muted-foreground">
                  No hay ajustes configurados. Ambos valores son iguales.
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Puedes configurar ajustes en la pestaña de EBITDA.
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* BLOQUE 1: Ratios de Liquidez */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <Droplets className="h-5 w-5 text-blue-500" />
            <CardTitle>Bloque 1 - Ratios de Liquidez</CardTitle>
          </div>
          <CardDescription>Capacidad de la empresa para hacer frente a sus obligaciones a corto plazo</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <RatioCard
              title="Liquidez Principal"
              value={ratiosLiquidez.liquidez_principal}
              format="number"
              description="Activo Corriente / Pasivo Corriente"
              benchmark={{ good: 1.5, warning: 1 }}
              helpText="Mide cuántos euros de activo corriente hay por cada euro de deuda a corto plazo. >1,5 buena liquidez; 1-1,5 ajustada; <1 riesgo de tensión de tesorería."
            />
            <RatioCardWithBreakdown
              title="Fondo de Maniobra"
              value={ratiosLiquidez.fondo_maniobra}
              format="currency"
              formula="Activo Corriente - Pasivo Corriente"
              breakdown={[
                { label: "Activo Corriente", value: balanceResumen.activo_corriente, format: "currency" },
                { label: "Pasivo Corriente", value: balanceResumen.pasivo_corriente, format: "currency", subtract: true }
              ]}
              helpText="Positivo indica capacidad para cubrir deudas a C/P"
            />
            <RatioCardWithBreakdown
              title="NOF"
              value={ratiosLiquidez.nof}
              format="currency"
              formula="Existencias (B.I) + Deudores (B.II) − Acreedores (C.IV) — mismo motor que la pestaña NOF"
              breakdown={[
                { label: "Existencias (netas)", value: balanceResumen.existencias, format: "currency" },
                { label: "Deudores", value: balanceResumen.clientes, format: "currency", add: true },
                { label: "Acreedores", value: balanceResumen.proveedores, format: "currency", subtract: true },
                ...(ratiosLiquidez.nof_ajustada != null
                  ? [{ label: "NOF ajustada (con tus exclusiones)", value: ratiosLiquidez.nof_ajustada, format: "currency" }]
                  : [])
              ]}
              helpText="Necesidades Operativas de Fondos del motor único (pestaña NOF): partidas del balance netas y con su signo. La ajustada descuenta las cuentas que has excluido del ciclo operativo (intercompany, dudoso cobro…) y el importe manual."
            />
            <div className="p-4 rounded-lg bg-muted/30">
              <div className="text-sm text-muted-foreground">
                <p className="font-medium mb-1">Interpretación general:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li><span className="text-emerald-600">Liquidez {">"} 1.5</span>: Buena capacidad de pago</li>
                  <li><span className="text-amber-600">Liquidez 1-1.5</span>: Ajustada, vigilar</li>
                  <li><span className="text-rose-600">Liquidez {"<"} 1</span>: Riesgo de impago</li>
                </ul>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* BLOQUE 2: Ratio de Solvencia */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-emerald-500" />
            <CardTitle>Bloque 2 - Ratio de Solvencia</CardTitle>
          </div>
          <CardDescription>Capacidad de la empresa para hacer frente a todas sus deudas</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-3">
            <RatioCard
              title="Solvencia Principal"
              value={ratiosSolvencia.solvencia_principal}
              format="number"
              description="Activo Total / Pasivo Total (exigible)"
              benchmark={{ good: 1.5, warning: 1.2 }}
              helpText="Mide cuántos euros de activo respaldan cada euro de deuda total. >1,5 buena garantía patrimonial; 1,2-1,5 adecuada; <1,2 riesgo de insolvencia (activo insuficiente para cubrir el exigible)."
            />
            <div className="col-span-2 p-4 rounded-lg bg-muted/30 flex items-center">
              <div className="text-sm text-muted-foreground">
                <p className="font-medium mb-1">Interpretación general:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>Solvencia {">"} 1.5: Excelente capacidad para cubrir deudas</li>
                  <li>Solvencia 1.2-1.5: Adecuada pero vigilar evolución</li>
                  <li>Solvencia {"<"} 1.2: Riesgo de insolvencia</li>
                </ul>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* BLOQUE 3: Ratios de Endeudamiento */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <CreditCard className="h-5 w-5 text-amber-500" />
            <CardTitle>Bloque 3 - Ratios de Endeudamiento</CardTitle>
          </div>
          <CardDescription>Estructura de financiación y capacidad de devolución de deuda</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-3 lg:grid-cols-6">
            <RatioCard
              title="DFN/EBITDA"
              value={ratiosEndeudamiento.dfn_ebitda}
              format="number"
              description={modo === "ajustado" ? "DFN / EBITDA ajustado" : "DFN / EBITDA base"}
              benchmark={{ good: 3, warning: 4 }}
              inverse
              helpText="Años teóricos que tardaría en devolver la deuda financiera neta dedicando todo el EBITDA. <3x cómodo; 3-4x vigilar; >4x apalancamiento elevado (orientativo, depende del sector)."
            />
            <RatioCard
              title="Deuda Fin./Cash Flow"
              value={ratiosEndeudamiento.deuda_cash_flow}
              format="number"
              description="Deuda Financiera / Cash Flow"
              benchmark={{ good: 4, warning: 6 }}
              inverse
              helpText="Años para devolver la deuda financiera con el cash flow generado (Resultado + Amortizaciones). Complementa al DFN/EBITDA usando flujo después de impuestos y financiero."
            />
            <RatioCardWithBreakdown
              title="Endeudamiento L/P"
              value={ratiosEndeudamiento.endeudamiento_lp}
              format="number"
              formula="Pasivo NC / Patrimonio Neto"
              breakdown={[
                { label: "Pasivo No Corriente", value: balanceResumen.pasivo_no_corriente, format: "currency" },
                { label: "Patrimonio Neto", value: balanceResumen.patrimonio_neto, format: "currency" }
              ]}
              helpText="Euros de deuda a largo plazo por cada euro de fondos propios. Mide el apalancamiento estructural; valores altos indican fuerte dependencia de financiación ajena a L/P."
            />
            <RatioCardWithBreakdown
              title="Endeudamiento C/P"
              value={ratiosEndeudamiento.endeudamiento_cp}
              format="number"
              formula="Pasivo C / Patrimonio Neto"
              breakdown={[
                { label: "Pasivo Corriente", value: balanceResumen.pasivo_corriente, format: "currency" },
                { label: "Patrimonio Neto", value: balanceResumen.patrimonio_neto, format: "currency" }
              ]}
              helpText="Euros de deuda a corto plazo por cada euro de fondos propios. Valores altos señalan dependencia de financiación que vence en menos de un año."
            />
            <RatioCard
              title="EBITDA/Gtos. Fin."
              value={ratiosEndeudamiento.ebitda_gastos_financieros}
              format="number"
              description={(modo === "ajustado" ? "EBITDA ajustado" : "EBITDA base") + " / Gastos Financieros"}
              benchmark={{ good: 4, warning: 2 }}
              helpText="Veces que el EBITDA cubre los gastos financieros del periodo. >4x holgado; 2-4x ajustado; <2x el resultado operativo apenas cubre los intereses."
            />
            <RatioCard
              title="Amortización/EBITDA"
              value={ratiosEndeudamiento.capex_ebitda}
              format="percent"
              description="Amortización / EBITDA (proxy CAPEX)"
              helpText="% del EBITDA equivalente a la amortización del ejercicio. Es una aproximación de la inversión de mantenimiento (proxy), NO el CAPEX real del periodo."
            />
          </div>
        </CardContent>
      </Card>

      {/* BLOQUE 4: Ratios de Rotación */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-purple-500" />
            <CardTitle>Bloque 4 - Plazos Medios</CardTitle>
          </div>
          <CardDescription>Eficiencia en la gestión del circulante</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-3">
            <RatioCardWithBreakdown
              title="Plazo Medio de Cobro"
              value={ratiosRotacion.plazo_cobro}
              format="days"
              formula="(Deudores / Ventas de la base) × días — motor NOF"
              breakdown={[
                { label: "Deudores (B.II)", value: balanceResumen.clientes, format: "currency" },
                { label: "Ventas de la base (70x menos excluidas)", value: nofModulo.bases?.ventas_base, format: "currency" },
                ...(ratiosRotacion.plazo_cobro_ajustado != null
                  ? [{ label: "PMC ajustado (sin saldos excluidos)", value: ratiosRotacion.plazo_cobro_ajustado, format: "days" }]
                  : [])
              ]}
              helpText="Días medios de cobro, con el motor de la pestaña NOF. El ajustado descuenta los saldos excluidos del ciclo; si marcas ventas fuera de la base (intercompany/AIE), la base también las descuenta."
            />
            <RatioCardWithBreakdown
              title="Plazo Medio de Pago"
              value={ratiosRotacion.plazo_pago}
              format="days"
              formula={ratiosRotacion.base_pmp_modo === "compras_servicios"
                ? "(Acreedores / Compras+Servicios 60+61+62) × días — motor NOF"
                : "(Acreedores / Compras 60+61) × días — motor NOF"}
              breakdown={[
                { label: "Acreedores (C.IV)", value: balanceResumen.proveedores, format: "currency" },
                { label: ratiosRotacion.base_pmp_modo === "compras_servicios" ? "Base: compras + servicios (60+61+62)" : "Base: compras (60+61)", value: pygResumen.compras_para_pago, format: "currency" },
                ...(ratiosRotacion.plazo_pago_ajustado != null
                  ? [{ label: "PMP ajustado", value: ratiosRotacion.plazo_pago_ajustado, format: "days" }]
                  : [])
              ]}
              helpText="Días que la empresa tarda en pagar, con la base configurada en el módulo NOF. Las bases son limpias: nunca incluyen deterioros (69x) ni tributos (631/634/636/639)."
            />
            <RatioCardWithBreakdown
              title="Plazo Medio de Existencias"
              value={ratiosRotacion.plazo_existencias}
              format="days"
              formula="(Existencias netas / Compras 60+61) × días — motor NOF"
              breakdown={[
                { label: "Existencias (netas de deterioro)", value: balanceResumen.existencias, format: "currency" },
                { label: "Compras (60+61, sin 693)", value: pygResumen.coste_ventas, format: "currency" },
                ...(ratiosRotacion.plazo_existencias_ajustado != null
                  ? [{ label: "PME ajustado", value: ratiosRotacion.plazo_existencias_ajustado, format: "days" }]
                  : [])
              ]}
              helpText="Días medios de rotación del stock, sobre el consumo real (60+61). El deterioro de existencias (693) no infla la base."
            />
          </div>
        </CardContent>
      </Card>

      {/* BLOQUE 5: Ratios de Personal */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <Users className="h-5 w-5 text-rose-500" />
            <CardTitle>Bloque 5 - Personal</CardTitle>
          </div>
          <CardDescription>Productividad y coste de personal</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2">
            <RatioCard
              title="Personal/Ventas"
              value={ratiosPersonal.personal_ventas}
              format="percent"
              description="Gastos de Personal / Ventas"
              helpText="Porcentaje de la cifra de negocios absorbido por el coste de personal. Indicador de productividad: cuanto menor, más eficiente es la estructura laboral respecto a las ventas."
            />
            <div className="p-4 rounded-lg bg-muted/30 flex items-center">
              <div className="text-sm text-muted-foreground">
                <p className="font-medium mb-1">Interpretación general:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li><span className="text-emerald-600">{"<"}30%</span>: Eficiente en sectores intensivos en capital</li>
                  <li><span className="text-amber-600">30-50%</span>: Normal en servicios</li>
                  <li><span className="text-rose-600">{">"}50%</span>: Alto, típico en consultoría</li>
                </ul>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function KPICard({ title, value, subtitle, icon: Icon, trend }) {
  return (
    <Card>
      <CardContent className="p-5">
        <div className="flex items-center justify-between gap-2">
          <p className="text-sm font-medium text-muted-foreground truncate">{title}</p>
          <div className={`h-8 w-8 shrink-0 rounded-lg inline-flex items-center justify-center ${trend === "up" ? "bg-emerald-50 dark:bg-emerald-950" : trend === "down" ? "bg-rose-50 dark:bg-rose-950" : "bg-muted"}`}>
            <Icon className={`h-4 w-4 ${trend === "up" ? "text-emerald-600" : trend === "down" ? "text-rose-600" : "text-muted-foreground"}`} />
          </div>
        </div>
        <p className="mt-2 text-[1.35rem] leading-tight font-semibold tracking-tight tabular-nums truncate" title={value}>{value}</p>
        {subtitle && (
          <p className="text-xs text-muted-foreground mt-1 truncate">{subtitle}</p>
        )}
      </CardContent>
    </Card>
  );
}

function RatioCard({ title, value, format, description, benchmark, inverse, helpText }) {
  let displayValue = "N/D";
  let statusColor = "text-muted-foreground";

  if (value !== null && value !== undefined && !isNaN(value)) {
    if (format === "percent") {
      displayValue = formatPercent(value);
    } else if (format === "days") {
      displayValue = `${formatNumber(value, 0)} días`;
    } else if (format === "currency") {
      displayValue = formatCurrency(value);
    } else {
      displayValue = formatNumber(value, 2);
    }

    if (benchmark) {
      // Sin abs en los ratios inversos: un valor NEGATIVO solo puede venir de
      // una DFN < 0 (caja neta), que es lo mejor, no "−5x = rojo". El backend
      // ya devuelve N/D con denominador ≤ 0 (dashboard.py).
      const numValue = inverse ? value : Math.abs(value);
      if (inverse) {
        if (numValue <= benchmark.good) statusColor = "text-emerald-600";
        else if (numValue <= benchmark.warning) statusColor = "text-amber-600";
        else statusColor = "text-rose-600";
      } else {
        if (numValue >= benchmark.good) statusColor = "text-emerald-600";
        else if (numValue >= benchmark.warning) statusColor = "text-amber-600";
        else statusColor = "text-rose-600";
      }
    }
  }

  return (
    <div className="p-4 rounded-lg bg-muted/50 hover:bg-muted/70 transition-colors">
      <div className="flex justify-between items-start">
        <p className="text-sm font-medium">{title}</p>
        {helpText && (
          <span className="text-xs text-muted-foreground ml-2" title={helpText}>ℹ️</span>
        )}
      </div>
      <p className={`text-xl font-mono font-bold mt-1 ${statusColor}`}>{displayValue}</p>
      <p className="text-xs text-muted-foreground mt-1">{description}</p>
    </div>
  );
}

function RatioCardWithBreakdown({ title, value, format, formula, breakdown, helpText }) {
  let displayValue = "N/D";
  let statusColor = "text-muted-foreground";

  if (value !== null && value !== undefined && !isNaN(value)) {
    if (format === "currency") {
      displayValue = formatCurrency(value);
      statusColor = value >= 0 ? "text-emerald-600" : "text-rose-600";
    } else if (format === "percent") {
      displayValue = formatPercent(value);
    } else if (format === "days") {
      displayValue = `${formatNumber(value, 0)} días`;
    } else {
      displayValue = formatNumber(value, 2);
    }
  }

  const formatBreakdownValue = (item) => {
    if (item.value === null || item.value === undefined) return "N/D";
    if (item.format === "currency") return formatCurrency(item.value);
    if (item.format === "percent") return formatPercent(item.value);
    if (item.format === "days") return `${formatNumber(item.value, 0)} días`;
    return formatNumber(item.value, 2);
  };

  return (
    <div className="p-4 rounded-lg bg-muted/50 hover:bg-muted/70 transition-colors">
      <div className="flex justify-between items-start">
        <p className="text-sm font-medium">{title}</p>
        {helpText && (
          <span className="text-xs text-muted-foreground ml-2" title={helpText}>ℹ️</span>
        )}
      </div>
      <p className={`text-xl font-mono font-bold mt-1 ${statusColor}`}>{displayValue}</p>
      <p className="text-xs text-muted-foreground mt-1 mb-2">{formula}</p>
      
      {/* Desglose con valores */}
      <div className="border-t pt-2 mt-2 space-y-1">
        {breakdown.map((item, idx) => (
          <div key={idx} className="flex justify-between text-xs">
            <span className="text-muted-foreground">
              {idx === 0 ? "" : item.subtract ? "−" : "+"} {item.label}
            </span>
            <span className="font-mono tabular-nums">
              {formatBreakdownValue(item)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
